import React, { useMemo } from "react";
import { Panel, Title, Text, Pill, Field, Skeleton, Icon } from "components/commons";
import { PillType } from "enums";
import lang from "translations";
import { v4 as uuidv4 } from "uuid";
import Timeline from "../../timeline";

const StatTile = ({ icon, label, value }) => (
  <div className="bg-gray-lightest rounded p-md flex items-start gap-3">
    <div className="bg-pelorous-lightest rounded p-xs flex items-center justify-center">
      <Icon name={icon} className="text-pelorous-dark" fontSize="16px" paddingless />
    </div>
    <div>
      <Text uppercase strong color="text-gray" size="text-xs">
        {label}
      </Text>
      <Title lg className="mt-xs">
        {value}
      </Title>
    </div>
  </div>
);

const ReservationOverview = ({ data, loading }) => {
  const reservation = data?.reservation || {};
  const guests = reservation.guests || [];

  const avgOrderDurationLabel = useMemo(() => {
    const orders = data?.orders || [];
    if (!orders.length) return "-";
    const total = orders.reduce((sum, order) => sum + (order.totalDurationSeconds || 0), 0);
    const avgSeconds = Math.round(total / orders.length);
    if (!avgSeconds) return "-";
    const hours = Math.floor(avgSeconds / 3600);
    const minutes = Math.floor((avgSeconds % 3600) / 60);
    const seconds = Math.floor(avgSeconds % 60);
    if (hours) return `${hours}h ${minutes}m`;
    if (minutes) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  }, [data]);

  if (loading) {
    return (
      <Panel className="mt-md">
        <Skeleton />
      </Panel>
    );
  }

  return (
    <Panel className="mt-md">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-md">
        <StatTile icon="burger-cup" label={lang.totalOrders} value={data?.orders?.length ?? 0} />
        <StatTile icon="bill" label={lang.totalBills} value={data?.bills?.length ?? 0} />
        <StatTile icon="sync" label={lang.reservationDuration} value={reservation.reservationDurationLabel || "-"} />
        <StatTile icon="sync" label={lang.avgOrderDuration} value={avgOrderDurationLabel} />
      </div>

      <div className="border border-gray-lightest rounded p-md">
        <div className="flex items-center gap-2 mb-sm">
          <Icon name="guest" className="text-pelorous" fontSize="16px" />
          <Text uppercase strong color="text-gray" size="text-xs">
            {lang.reservationDetails}
          </Text>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label={lang.resourceUsed}>
            <Text>{reservation.resourceName || "-"} {reservation.resourceId ? `(#${reservation.resourceId})` : ""}</Text>
          </Field>
          <Field label={lang.guestCount}>
            <Text>{reservation.guestCount ?? "-"}</Text>
          </Field>
          <Field label={lang.openedAt}>
            <Text>{reservation.openedAt ?? "-"}</Text>
          </Field>
          <Field label={lang.closedAt}>
            <Text>{reservation.closedAt ?? "-"}</Text>
          </Field>
          {reservation.closeReason && (
            <Field label={lang.closeReason}>
              <Text>{reservation.closeReason}</Text>
            </Field>
          )}
        </div>
        {guests.length > 0 && (
          <div className="mt-md flex flex-wrap gap-2">
            {guests.map((guest) => (
              <Pill key={uuidv4()} size="text-xs" type={guest.isPrimary ? PillType.Blue : PillType.Gray}>
                {guest.isPrimary && <Icon name="star" fontSize="9px" className="mr-xs" />}
                {guest.name}
              </Pill>
            ))}
          </div>
        )}
      </div>

      {reservation.resourceId && (
        <div className="border border-gray-lightest rounded p-md mt-md">
          <div className="flex items-center gap-2 mb-sm">
            <Icon name="dashboard1" className="text-pelorous" fontSize="16px" />
            <Text uppercase strong color="text-gray" size="text-xs">
              {lang.resourceActivityLog}
            </Text>
          </div>
          <Timeline entries={data?.resourceStatusHistory} />
        </div>
      )}
    </Panel>
  );
};

export default ReservationOverview;
